<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading :module-id="access.module_id" :profile-id="access.profile_id" :reference="access.view_reference" summary-only />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <AccountingHelp view="accountingClosing" />
            </div>
          </div>
        </section>
        <AccountingBookBar :businesses="arr.businesses" :allow-both="false" verb="Cerrás" note="Cada libro tiene su propio ejercicio: apertura, cierre y bloqueos." @change="onBookChange($event)" />
      </b-card>

      <!-- Sin apertura: el ciclo arranca acá -->
      <b-card v-if="needsOpening">
        <div class="closing-empty">
          <AppIcon name="edit_note" class="closing-empty__icon" />
          <div class="closing-empty__title">Este libro todavía no tiene apertura</div>
          <p class="small text-muted mb-3">La apertura inicial carga los saldos con los que arrancás y marca el inicio del primer ejercicio. Se hace una sola vez por empresa y libro.</p>
          <b-button variant="dark" size="sm" @click="$refs.openingForm.open()"><AppIcon name="edit_note" class="mr-1" />Cargar apertura inicial</b-button>
        </div>
      </b-card>

      <template v-if="status && !needsOpening">
        <!-- Dónde estamos -->
        <b-card>
          <div class="closing-period">
            <div class="closing-period__item">
              <small class="closing-label">Ejercicio en curso</small>
              <div class="closing-period__value">Desde el {{ formatDate(status.period_start) }}</div>
            </div>
            <div class="closing-period__item">
              <small class="closing-label">Último cierre</small>
              <div v-if="status.last_closing">
                {{ formatDate(status.last_closing.date) }} · {{ status.last_closing.reference }}
              </div>
              <div class="text-muted" v-else>Todavía no hay cierres</div>
            </div>
            <div class="closing-period__item">
              <small class="closing-label">Bloqueo mensual</small>
              <div v-if="status.locked_until">Hasta el {{ formatDate(status.locked_until) }}</div>
              <div class="text-muted" v-else>Sin meses bloqueados</div>
            </div>
            <div class="closing-period__actions" v-if="status.last_closing && access.elements.reopenPeriod">
              <span v-b-tooltip.hover :title="status.reopen_block || ''">
                <b-button variant="outline-danger" size="sm" :disabled="!!status.reopen_block" @click="openReopen()">
                  <AppIcon name="unlock" class="mr-1" />Reabrir último cierre
                </b-button>
              </span>
            </div>
          </div>
          <small class="text-muted d-block mt-2">El bloqueo mensual, el inventario y la apertura inicial se manejan desde Movimientos Contables, en el botón +.</small>

          <!-- Un cierre quedó sin su apertura: se completa antes de seguir -->
          <b-alert show variant="warning" class="mt-3 mb-0" v-if="status.last_closing && !status.next_opening">
            El cierre del {{ formatDate(status.last_closing.date) }} todavía no tiene la apertura del ejercicio siguiente.
            <b-button variant="dark" size="sm" class="ml-2" :disabled="loading" @click="openMissingOpening()">Generar apertura</b-button>
          </b-alert>

          <div class="closing-status__history" v-if="status.reopenings.length">
            <small class="closing-label">Reaperturas</small>
            <div v-for="item in status.reopenings" :key="item.id" class="small">
              {{ moment(item.created_at).format('DD/MM/YYYY HH:mm') }} · Cierre {{ formatDate(item.closing_date) }}<span v-if="item.user_name"> · {{ item.user_name }}</span> · {{ item.reason }}
            </div>
          </div>
        </b-card>

        <!-- Cerrar el ejercicio -->
        <b-card v-if="!(status.last_closing && !status.next_opening)">
          <h5 class="mb-3">Cerrar el ejercicio y abrir el siguiente</h5>

          <!-- 1. Fechas -->
          <div class="closing-step">
            <div class="closing-step__num">1</div>
            <div class="closing-step__body">
              <div class="closing-step__title">Fechas</div>
              <div class="closing-dates">
                <div>
                  <label class="closing-field-label">Cierre</label>
                  <b-form-input type="date" size="sm" v-model="filters.date_close" :min="status.period_start" @change="onCloseDateChange()"></b-form-input>
                </div>
                <AppIcon name="forward" class="closing-dates__arrow" />
                <div>
                  <label class="closing-field-label">Apertura del siguiente</label>
                  <b-form-input type="date" size="sm" v-model="filters.date_open" @change="preview()"></b-form-input>
                </div>
              </div>
              <small class="text-muted">Se cierra lo registrado del {{ formatDate(status.period_start) }} al {{ formatDate(filters.date_close) }}.</small>
            </div>
          </div>

          <!-- 2. Controles -->
          <div class="closing-step">
            <div class="closing-step__num">2</div>
            <div class="closing-step__body">
              <div class="closing-step__title">Controles <b-spinner small class="ml-1" v-if="loading && !previewData" /></div>
              <ul class="closing-checks" v-if="previewData">
                <li :class="previewData.closing_block ? 'closing-check--error' : 'closing-check--ok'">
                  <AppIcon :name="previewData.closing_block ? 'close' : 'check'" />
                  <span>{{ previewData.closing_block || 'La fecha de cierre es válida' }}</span>
                </li>
                <li :class="previewData.pending_count ? 'closing-check--error' : 'closing-check--ok'">
                  <AppIcon :name="previewData.pending_count ? 'close' : 'check'" />
                  <span v-if="previewData.pending_count">
                    Hay {{ previewData.pending_count }} movimiento/s sin asiento hasta esa fecha.
                    <router-link :to="{ name: 'AccountingStaffAccountingPending' }">Aprobarlos en Asientos Pendientes</router-link>
                  </span>
                  <span v-else>No hay movimientos sin asiento</span>
                </li>
                <li v-if="previewData.inventory_warning" class="closing-check--warning">
                  <AppIcon name="info" />
                  <span>
                    {{ previewData.inventory_warning }}
                    <router-link :to="{ name: 'AccountingStaffAccountingEntries' }">Ir a Movimientos Contables</router-link>
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <!-- 3. Resultado -->
          <div class="closing-step" v-if="previewData">
            <div class="closing-step__num">3</div>
            <div class="closing-step__body">
              <div class="closing-step__title">Resultado del ejercicio</div>
              <div class="closing-result" :class="previewData.closing.net_result >= 0 ? 'closing-result--gain' : 'closing-result--loss'">
                <small>{{ previewData.closing.net_result >= 0 ? 'Ganancia' : 'Pérdida' }}</small>
                <b>{{ formatCurrency(Math.abs(previewData.closing.net_result)) }}</b>
              </div>
              <div>
                <b-button variant="link" size="sm" class="px-0 mr-3" @click="show.closing = !show.closing">
                  <AppIcon :name="show.closing ? 'chevron-up' : 'chevron-down'" class="mr-1" />Asiento de cierre ({{ previewData.closing.lines.length }} cuentas)
                </b-button>
                <b-button variant="link" size="sm" class="px-0" @click="show.opening = !show.opening">
                  <AppIcon :name="show.opening ? 'chevron-up' : 'chevron-down'" class="mr-1" />Asiento de apertura ({{ previewData.opening.lines.length }} cuentas)
                </b-button>
              </div>

              <div v-if="show.closing" class="mt-2">
                <small class="text-muted d-block mb-1">Salda Ingresos y Egresos contra Resultado del Ejercicio, al {{ formatDate(filters.date_close) }}.</small>
                <b-table class="app-table app-table--compact mb-0" :items="previewData.closing.lines" :fields="tableFields" small sticky-header="320px">
                  <template v-slot:cell(account)="data"><span :class="{ 'pl-4': data.item.type == 'Haber' }">{{ data.item.accounting_accounts_code }} · {{ data.item.accounting_accounts_name }}</span></template>
                  <template v-slot:cell(debit)="data"><b v-if="data.item.type == 'Debe'">{{ formatCurrency(data.item.amount) }}</b></template>
                  <template v-slot:cell(credit)="data"><b v-if="data.item.type == 'Haber'">{{ formatCurrency(data.item.amount) }}</b></template>
                </b-table>
              </div>
              <div v-if="show.opening" class="mt-2">
                <small class="text-muted d-block mb-1">Reabre Activo, Pasivo y Patrimonio con los saldos al cierre, al {{ formatDate(filters.date_open) }}.</small>
                <b-table class="app-table app-table--compact mb-0" :items="previewData.opening.lines" :fields="tableFields" small sticky-header="320px">
                  <template v-slot:cell(account)="data"><span :class="{ 'pl-4': data.item.type == 'Haber' }">{{ data.item.accounting_accounts_code }} · {{ data.item.accounting_accounts_name }}</span></template>
                  <template v-slot:cell(debit)="data"><b v-if="data.item.type == 'Debe'">{{ formatCurrency(data.item.amount) }}</b></template>
                  <template v-slot:cell(credit)="data"><b v-if="data.item.type == 'Haber'">{{ formatCurrency(data.item.amount) }}</b></template>
                </b-table>
              </div>
            </div>
          </div>

          <!-- 4. Confirmar -->
          <div class="closing-step" v-if="previewData">
            <div class="closing-step__num">4</div>
            <div class="closing-step__body">
              <div class="closing-step__title">Confirmar</div>
              <b-row class="align-items-end">
                <b-col md="3">
                  <b-form-group label="Referencia del cierre" class="mb-2">
                    <b-form-input type="text" size="sm" v-model="crud.closing.reference"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group label="Referencia de la apertura" class="mb-2">
                    <b-form-input type="text" size="sm" v-model="crud.opening.reference"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="6" class="mb-2 text-md-right">
                  <b-button variant="dark" :disabled="loading || !canClose" @click="confirmClose()">
                    <AppIcon name="lock" class="mr-1" />Cerrar ejercicio y abrir el siguiente
                  </b-button>
                </b-col>
              </b-row>
              <small class="text-muted">El ejercicio cerrado queda bloqueado: no admite comprobantes ni asientos hasta reabrirlo.</small>
            </div>
          </div>
        </b-card>
      </template>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" />
            Volver
          </b-button>
        </div>
      </b-card>

      <b-modal v-model="modal.reopen.active" :key="modal.reopen._id" :id="modal.reopen._id">
        <div slot="modal-header" minimize class="app-modal-header app-modal-header--danger">
          <span class="app-modal-header__icon"><AppIcon name="unlock" /></span>
          <h5 class="app-modal-header__title">Reabrir último cierre</h5>
        </div>

        <div v-if="status && status.last_closing">
          <p class="mb-2">
            Se borra el cierre del <b>{{ formatDate(status.last_closing.date) }}</b> ({{ status.last_closing.reference }})<span v-if="status.next_opening"> y la apertura del <b>{{ formatDate(status.next_opening.date) }}</b></span>.
            El período vuelve a admitir comprobantes y asientos.
          </p>
          <b-form-group label="Motivo *" class="mb-0">
            <b-form-textarea v-model="modal.reopen.reason" rows="3" placeholder="Por qué se reabre el período" />
          </b-form-group>
        </div>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" size="sm" class="mr-1" @click="modal.reopen.active = false"><AppIcon name="close" class="mr-1" />Cancelar</b-button>
          <b-button variant="danger" size="sm" :disabled="loading || !modal.reopen.reason.trim()" @click="reopen()"><AppIcon name="unlock" class="mr-1" />Reabrir</b-button>
        </div>
      </b-modal>
      <AccountingOpeningForm ref="openingForm" :business-id="filters.business_id" :declared="filters.declared" @saved="onOpeningSaved()" />
    </b-col>
  </b-row>
</template>
<script>
  import AccountingHelp from './inc/AccountingHelp'
  import AccountingOpeningForm from './inc/AccountingOpeningForm'
  import AccountingBookBar from './inc/AccountingBookBar'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Helper from '@/handler/helper'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import moment from 'moment'

  export default {
    components: {
      AccountingBookBar,
      AccountingHelp,
      AccountingOpeningForm,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.CONTABILIDAD,
          profile_id: Profiles.PERSONAL,
          view_reference: 'accountingClosing',
          elements: {}
        },
        filters: {
          date_close: '',
          date_open: '',
          business_id: null,
          declared: null,
        },
        arr: {
          businesses: [],
        },
        tableFields: [
          { key: 'account', label: 'Cuenta', class: 'align-middle' },
          { key: 'debit',   label: 'Debe',   class: 'align-middle text-right' },
          { key: 'credit',  label: 'Haber',  class: 'align-middle text-right' },
        ],
        previewData: null,
        status: null,
        show: { closing: false, opening: false },
        modal: {
          reopen: { active: false, _id: '', reason: '' },
        },
        loading: false,
        crud: {
          closing: { reference: '', observations: 'Asiento de cierre de ejercicio' },
          opening: { reference: '', observations: 'Asiento de apertura de ejercicio' },
        },
      }
    },
    created() {
      Helper.hasAccessView(this.access)
      this.access.elements.reopenPeriod = Helper.hasAccessElement(this.access, 'reopen_period')
    },
    mounted() {
      this._start = true
      this.loadBusinesses()
    },
    // Con keep-alive, al volver: el estado pudo cambiar desde otras pantallas
    activated() {
      if(this._start) {
        this._start = false
        return false
      }

      this.refresh(false)
    },
    computed: {
      needsOpening() {
        return !!this.status && !this.status.period_start
      },
      canClose() {
        return !!this.previewData
            && !this.previewData.closing_block
            && !this.previewData.pending_count
            && !!this.crud.closing.reference.trim()
            && !!this.crud.opening.reference.trim()
            && !!this.filters.date_open
      },
    },
    methods: {
      moment,
      loadBusinesses() {
        serviceAPI.obtenerEmpresas()
          .then(response => {
            const items = response.data || []
            this.arr.businesses = items.map(b => ({ value: b.id, text: b.name || b.reference || String(b.id) }))
          })
          .catch(error => {
            this.$awn.alert(Error.showError(error))
          })
      },
      onBookChange(book) {
        this.filters.business_id = book.business_id
        this.filters.declared = book.declared
        this.previewData = null
        this.refresh(true)
      },
      onOpeningSaved() {
        this.refresh(true)
      },
      // Estado del libro y, si corresponde, la vista previa del cierre
      refresh(applyDates) {
        return this.loadStatus(applyDates).then(() => this.preview())
      },
      // Las fechas se sugieren al elegir el libro: después son las que eligió el usuario
      loadStatus(applyDates = false) {
        if(!this.filters.business_id || this.filters.declared === null) {
          this.status = null
          return Promise.resolve()
        }
        return serviceAPI.obtenerEstadoPeriodo({
          business_id: this.filters.business_id,
          declared:    this.filters.declared,
        })
        .then(response => {
          this.status = response.data
          if(applyDates) this.applyPeriodDates()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      // El ejercicio arranca en la apertura. El cierre se sugiere a fin de ese año.
      applyPeriodDates() {
        const start = this.status.period_start
        if(!start) return

        const year = moment(start).year()
        this.filters.date_close = year + '-12-31'
        this.filters.date_open  = (year + 1) + '-01-01'
        this.crud.closing.reference = 'CIERRE-' + year
        this.crud.opening.reference = 'APERTURA-' + (year + 1)
      },
      onCloseDateChange() {
        if(this.filters.date_close) {
          const close = moment(this.filters.date_close)
          const open = close.clone().add(1, 'day')
          this.filters.date_open = open.format('YYYY-MM-DD')
          this.crud.closing.reference = 'CIERRE-' + close.year()
          this.crud.opening.reference = 'APERTURA-' + open.year()
        }
        this.preview()
      },
      // Se recalcula sola con cada cambio: controles, resultado y asientos
      preview() {
        if(!this.status || this.needsOpening || !this.filters.date_close || (this.status.last_closing && !this.status.next_opening)) {
          this.previewData = null
          return
        }
        this.loading = true
        serviceAPI.previewCierreApertura({
          date_close:  this.filters.date_close,
          date_open:   this.filters.date_open,
          business_id: this.filters.business_id,
          declared:    this.filters.declared,
        })
        .then(response => {
          this.previewData = response.data
        })
        .catch(error => {
          this.previewData = null
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      confirmClose() {
        const data = this.previewData.closing
        const result = data.net_result >= 0 ? 'Ganancia' : 'Pérdida'
        const message = 'Se cierra el ejercicio del ' + this.formatDate(this.status.period_start) + ' al ' + this.formatDate(this.filters.date_close)
                      + ' (' + result + ': ' + this.formatCurrency(Math.abs(data.net_result)) + ') y se abre el siguiente el ' + this.formatDate(this.filters.date_open)
                      + '. El ejercicio cerrado queda bloqueado.'
        const title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: 'lock' } }),
          'Cerrar ejercicio'
        ])
        this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'dark',
          okTitle: 'Cerrar y abrir',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          headerClass: 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
        .then(value => {
          if(value) this.closeAndOpen()
        })
      },
      // Cierre y apertura van juntos. Si la apertura falla, el cierre queda y la pantalla ofrece completarla.
      closeAndOpen() {
        const dateOpen = this.filters.date_open
        this.loading = true
        serviceAPI.generarCierre({
          date_close:   this.filters.date_close,
          reference:    this.crud.closing.reference,
          observations: this.crud.closing.observations,
          business_id:  this.filters.business_id,
          declared:     this.filters.declared,
        })
        .then(() => this.generateOpening())
        .then(() => {
          this.$awn.success('Ejercicio cerrado. El siguiente arranca el ' + this.formatDate(dateOpen))
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
          this.refresh(true)
        })
      },
      generateOpening() {
        return serviceAPI.generarApertura({
          date_close:   this.filters.date_close,
          date_open:    this.filters.date_open,
          reference:    this.crud.opening.reference,
          observations: this.crud.opening.observations,
          business_id:  this.filters.business_id,
          declared:     this.filters.declared,
        })
      },
      // Completa la apertura de un cierre que quedó sin ella
      openMissingOpening() {
        const close = moment(this.status.last_closing.date)
        const open = close.clone().add(1, 'day')
        this.filters.date_close = close.format('YYYY-MM-DD')
        this.filters.date_open = open.format('YYYY-MM-DD')
        this.crud.opening.reference = 'APERTURA-' + open.year()
        this.loading = true
        this.generateOpening()
        .then(() => {
          this.$awn.success('Apertura generada')
          return this.refresh(true)
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      openReopen() {
        this.modal.reopen.reason = ''
        this.modal.reopen._id = Math.random().toString(36).slice(2, 8)
        this.modal.reopen.active = true
      },
      reopen() {
        this.loading = true
        serviceAPI.reabrirPeriodo({
          closing_id: this.status.last_closing.id,
          reason:     this.modal.reopen.reason,
        })
        .then(() => {
          this.modal.reopen.active = false
          this.$awn.success('Período reabierto')
          return this.refresh(true)
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      formatDate(date) {
        return date ? moment(date).format('DD/MM/YYYY') : ''
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value || 0)
      },
    }
  }
</script>
<style scoped>
  .closing-label {
    display: block;
    font-weight: 600;
    color: var(--app-color-text-secondary);
  }
  .closing-empty {
    padding: 24px 12px;
    text-align: center;
  }
  .closing-empty__icon {
    font-size: 32px;
    color: var(--app-color-text-muted);
  }
  .closing-empty__title {
    font-size: 16px;
    font-weight: 700;
    margin: 6px 0 4px;
  }
  .closing-period {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px 32px;
  }
  .closing-period__value {
    font-size: 18px;
    font-weight: 700;
  }
  .closing-period__actions {
    margin-left: auto;
  }
  .closing-status__history {
    margin-top: 12px;
    padding-top: 8px;
    border-top: 1px solid var(--app-color-border);
  }
  .closing-step {
    display: flex;
    gap: 12px;
    padding: 12px 0;
    border-top: 1px solid var(--app-color-border);
  }
  .closing-step__num {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    color: var(--app-color-white);
    background: var(--app-color-text);
    border-radius: 50%;
  }
  .closing-step__body {
    flex: 1 1 auto;
    min-width: 0;
  }
  .closing-step__title {
    font-weight: 700;
    margin-bottom: 6px;
  }
  .closing-dates {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 8px 12px;
    margin-bottom: 4px;
  }
  .closing-dates__arrow {
    margin-bottom: 6px;
    color: var(--app-color-text-muted);
  }
  .closing-field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
    color: var(--app-color-text-secondary);
  }
  .closing-checks {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .closing-checks li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 4px 0;
    font-size: 14px;
  }
  .closing-checks li span {
    color: var(--app-color-text);
  }
  .closing-check--ok {
    color: var(--app-color-success);
  }
  .closing-check--error {
    color: var(--app-color-danger);
  }
  .closing-check--warning {
    color: var(--app-color-warning);
  }
  .closing-result {
    display: inline-block;
    margin-bottom: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    border-left: 4px solid;
  }
  .closing-result small {
    display: block;
    font-weight: 600;
    color: var(--app-color-text-secondary);
  }
  .closing-result b {
    font-size: 20px;
  }
  .closing-result--gain {
    background: var(--app-color-success-soft);
    border-left-color: var(--app-color-success);
  }
  .closing-result--loss {
    background: var(--app-color-danger-soft);
    border-left-color: var(--app-color-danger);
  }
</style>
